import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import type { ApplicantCount } from '@seek/ca-graphql-schema/types';
import type { SourcrRecruiterExternalReference } from '@seek/chalice-types';
import type { JobActionOrigin } from '@seek/seek-jobs-analytics';
import { addTags, getJobDetailsMetadata, type SolMetadata } from '@seek/sol-js';
import { parse } from 'query-string';

import {
  isBrowserAnalyticsFacade,
  isUniversalAnalyticsFacade,
  type AnalyticsFacade,
  type BrowserAnalyticsFacade,
} from 'src/modules/AnalyticsFacade';
import type {
  JobDetails,
  PersonalisedJobDetails,
} from 'src/store/jobdetails/types';
import type { ChaliceStore } from 'src/store/types';
import type { RedialLocals } from 'src/types/RedialLocals';
import { mapDisplayTagsType } from 'src/utils/mapJobTagsType';

import { selectExperimentsAsSolTags } from '../store/experiments/experimentHelpers';
import {
  getTestScope,
  isTestAgent,
} from '../utils/productionTesting/productionTesting';

interface OptionsType {
  jobDetails: JobDetails | null;
  correlationId: string;
  getSolMetadata: () => SolMetadata;
  jobViewOrigin?: string;
  jobViewType: 'standalone' | 'split' | 'preview' | 'drawer';
  jobListingType?: string;
  jobActionOrigin?: string;
  jobListingPosition?: number;
  omnitureTracking?: string;
  isFeatured?: boolean;
  sourcrRecruiterOnJobCard?: SourcrRecruiterExternalReference;
  jobApplicantsVolume?: number | undefined;
}

export const createGetSolMetadataForJDP =
  (options: {
    experiments: ChaliceStore['experiments'];
    hash: ChaliceStore['location']['hash'];
    jobId?: number | string;
    query: RedialLocals['query'];
  }) =>
  (): SolMetadata => {
    if (!options.jobId) {
      return {};
    }

    const lowercaseKeys = (record: Record<string, any>) =>
      Object.entries(record).reduce<Record<string, any>>(
        (memo, [key, value]) => ({
          ...memo,
          [key.toLowerCase()]: value,
        }),
        {},
      );
    // After the react router v6 upgrade, the hash will be empty in the client route, so we need to get it from the window object
    const windowHash =
      typeof window !== 'undefined' ? window.location?.hash : '';
    const hash = ((options.hash || windowHash) ?? '').replace('#', '');
    const hashValues = lowercaseKeys(parse(hash));
    const queryValues = lowercaseKeys(options.query || {});
    const baseData = getJobDetailsMetadata({
      jobId: options.jobId.toString(),
      searchRequestToken:
        queryValues.searchrequesttoken ?? hashValues.searchRequestToken,
      token: queryValues.token ?? hashValues.token,
      key: queryValues.sol ?? hashValues.sol,
    });
    const experimentTags = selectExperimentsAsSolTags({
      experiments: options.experiments,
    });
    const taggedData = addTags(baseData, experimentTags);
    return taggedData;
  };

export type HubbleJDVEventTriggerTag =
  | 'default'
  | 'auto_display'
  | 'pre_selected';

export const getIsAddSalaryPrefNudgeShown = ({
  isAuthenticated,
  isSalaryLabel,
  salaryMatch,
}: {
  isAuthenticated?: boolean;
  isSalaryLabel?: boolean;
  salaryMatch?: NonNullable<PersonalisedJobDetails>['salaryMatch'];
}) =>
  (!isAuthenticated && !isSalaryLabel) ||
  salaryMatch?.__typename === 'JobProfileMissingSalaryPreference';

export const getIsSalaryMatch = (
  salaryMatch?: NonNullable<PersonalisedJobDetails>['salaryMatch'],
) => salaryMatch?.__typename === 'JobProfileSalaryMatch';

export function trackJobDetailsLoaded(
  analyticsFacade: AnalyticsFacade,
  options: OptionsType,
  state: ChaliceStore,
  apolloClient: ApolloClient<NormalizedCacheObject>,
  eventTrigger?: HubbleJDVEventTriggerTag,
) {
  const isAuthenticated = Boolean(state.user?.authenticated);
  const {
    jobListingType,
    jobDetails,
    jobViewOrigin,
    jobActionOrigin,
    isFeatured,
    sourcrRecruiterOnJobCard,
  } = options || {};
  const solMetadata = options.getSolMetadata();

  const TopApplicantBadge: Array<'TOP_APPLICANT'> = Boolean(
    state?.jobdetails?.personalised?.topApplicantBadge,
  )
    ? ['TOP_APPLICANT']
    : [];
  const jdvDisplayTags = [
    ...TopApplicantBadge,
    ...mapDisplayTagsType(jobDetails?.job?.products?.displayTags),
  ];

  const isAddSalaryPrefNudgeShown = getIsAddSalaryPrefNudgeShown({
    isAuthenticated,
    isSalaryLabel: Boolean(state.jobdetails.result?.job.salary?.label),
    salaryMatch: state?.jobdetails?.personalised?.salaryMatch,
  });

  const isSalaryMatch = getIsSalaryMatch(
    state?.jobdetails?.personalised?.salaryMatch,
  );

  const applicantCountInsight =
    (jobDetails?.insights?.[0] as ApplicantCount) ?? {};

  const jobApplicantsVolume = Object.hasOwn(options, 'jobApplicantsVolume')
    ? options.jobApplicantsVolume
    : applicantCountInsight?.count;

  const universalAnalyticsPayload: Parameters<
    (typeof analyticsFacade)['jobDetailsLoaded']
  >[0] = {
    brand: state.appConfig.brand,
    isApplied: Boolean(
      state?.jobdetails?.personalised?.appliedDateTime?.shortAbsoluteLabel,
    ),
    isAddSalaryPrefNudgeShown,
    isFeatured,
    isHirerVerified:
      Boolean(state?.jobdetails?.result?.job?.isVerified) ?? false,
    isSalaryMatch,
    flightId: solMetadata?.tags?.mordor__flights ?? '',
    jobActionOrigin: jobActionOrigin as JobActionOrigin,
    jobBadges: jdvDisplayTags,
    jobListingPosition: options.jobListingPosition,
    jobCardSellPointCount: options.jobDetails?.job?.products?.bullets?.length,
    ...(jobListingType && { jobListingType }),
    jobViewOriginQuery: jobViewOrigin,
    jobViewType: options.jobViewType,
    ...(jobDetails?.hasOwnProperty('sourcr') && {
      opportunityType: 'recruiter',
    }),
    searchRemoteOptions: state.search.query?.workarrangement,
    isMatchedSkillsNudgeShown: !isAuthenticated,
    jobBadgesFilter: state?.search?.query?.tags
      ? [state?.search?.query?.tags]
      : [],
    hasSourcr: Boolean(jobDetails?.sourcr),
    jobApplicantsVolume,
  };

  if (jobDetails) {
    if (isUniversalAnalyticsFacade(analyticsFacade)) {
      analyticsFacade.jobDetailsLoaded(universalAnalyticsPayload);
    } else if (isBrowserAnalyticsFacade(analyticsFacade)) {
      const SOL_EVENT_TRIGGER_TAG = 'sol:eventTrigger';
      solMetadata.tags = {
        ...solMetadata.tags,
        [SOL_EVENT_TRIGGER_TAG]: eventTrigger ?? 'default',
      };

      const hubblePayload: Parameters<
        InstanceType<typeof BrowserAnalyticsFacade>['jobDetailsLoaded']
      >[1] = {
        // Application tips is not currently implemented in Chalice
        applicationTipsType: 'NotShown',
        isSalaryMatch,
        isAddSalaryPrefNudgeShown,
        jobBadges: jdvDisplayTags,
        jobId: jobDetails?.job?.id,
        linkout: jobDetails?.job?.isLinkOut,
        solMetadata,
        ...(sourcrRecruiterOnJobCard
          ? { recruiterId: sourcrRecruiterOnJobCard.id }
          : {}),
      };

      const testHeaders = state.user.testHeaders;
      const isTestUser = isTestAgent(testHeaders);

      const tags = isTestUser
        ? {
            testRecord: isTestUser,
            testScope: getTestScope(testHeaders),
          }
        : {};

      const eventCapturePayload: Parameters<
        InstanceType<typeof BrowserAnalyticsFacade>['jobDetailsLoaded']
      >[2] = {
        candidateId: state.user.seekerId,
        omnitureTracking: options.omnitureTracking,
        zone: state.appConfig.zone,
        jobDetailsViewedCorrelationId: options.correlationId,
        tags,
        isFeatured,
      };

      analyticsFacade.jobDetailsLoaded(
        universalAnalyticsPayload,
        hubblePayload,
        eventCapturePayload,
        apolloClient,
        state.user.sessionId,
      );
    }
  }
}
